import { Check, ExternalLink, X } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import InsurerLogo from '@/components/shared/InsurerLogo';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isPriceUnavailable } from '@/services/utils';
import type { QuoteItem } from '@/types/app';

interface QuoteProps {
	quote: QuoteItem;
	productType: string;
	isSelected: boolean;
	isDisabled: boolean;
	isTransitioning: boolean;
	onSelect: (quoteId: string) => void;
	/** Read-only presentation: keeps the selected styling but removes the choose/deselect controls and card interaction. */
	readOnly?: boolean;
	/** Also show the premium rate. Off in the customer flow, on for specialist audiences. */
	showPremiumRate?: boolean;
}

const Quote = ({
	quote,
	productType,
	isSelected,
	isDisabled,
	isTransitioning,
	onSelect,
	readOnly = false,
	showPremiumRate = false
}: QuoteProps): React.JSX.Element => {
	const { t } = useTranslation();
	const isUnavailable = isPriceUnavailable(quote.total);
	const totalEur = quote.total ?? 0;
	const isBusy = isDisabled || isTransitioning || isUnavailable;
	// Specialist audiences see the premium rate as the headline instead of the
	// amount — the priced package overview at the bottom of the page carries the euros.
	const showRateHeadline = showPremiumRate && !isUnavailable && quote.premium_rate_percentage > 0;
	const API_URL = import.meta.env.VITE_API_BASE_URL as string;

	const hasSaving =
		quote.saving_percentage != null &&
		quote.saving_percentage > 0 &&
		quote.saving_amount != null &&
		quote.saving_amount > 0;
	let quoteCardAriaLabel = t('productSelection.quoteCard.select', { name: quote.insurer_name });
	let buttonLabel = t('productSelection.quoteCard.choose');

	if (isUnavailable) {
		quoteCardAriaLabel = t('quotes.item.notAvailable');
	} else if (isSelected) {
		quoteCardAriaLabel = t('productSelection.quoteCard.deselect', { name: quote.insurer_name });
		buttonLabel = t('productSelection.quoteCard.chosen');
	}

	function handleSelect(): void {
		if (isBusy) return;
		onSelect(quote.quote_id);
	}

	function handleKeyDown(e: React.KeyboardEvent): void {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleSelect();
		}
	}

	// Read-only cards are purely presentational: no click target, no button role.
	const interactiveProps = readOnly
		? {}
		: {
				onClick: handleSelect,
				onKeyDown: handleKeyDown,
				tabIndex: isBusy ? -1 : 0,
				role: 'button' as const,
				'aria-label': quoteCardAriaLabel
			};

	return (
		<div className="group relative h-full w-full" data-testid="quote-item" data-cy="quote-item">
			{isSelected && !isUnavailable && (
				<div className="absolute -top-2 -right-2 z-10">
					{/* Without the button role and its aria-label, the checkmark is the only
					    selection cue left, so give it a text alternative. */}
					{readOnly && <span className="sr-only">{t('quotes.item.selected')}</span>}
					<div
						className={cn(
							'w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow-md',
							!readOnly && 'group-hover:hidden [@media(hover:none)]:hidden'
						)}
					>
						<Check className="w-3 h-3 text-white" />
					</div>
					{!readOnly && (
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								handleSelect();
							}}
							disabled={isBusy}
							className="w-5 h-5 rounded-full bg-muted-foreground/60 hover:bg-muted-foreground items-center justify-center shadow-md hidden group-hover:flex [@media(hover:none)]:flex cursor-pointer"
							aria-label={t('productSelection.quoteCard.deselect', { name: quote.insurer_name })}
						>
							<X className="w-3 h-3 text-white" />
						</button>
					)}
				</div>
			)}

			<div
				className={cn(
					'border rounded-lg overflow-hidden bg-white',
					isSelected && !isUnavailable ? 'border-green-500' : 'border-border',
					isUnavailable && 'opacity-75'
				)}
				data-testid="quote-item-card"
				data-cy="quote-item-card"
			>
				<div
					className={cn(
						'flex flex-col items-center p-5 pt-7 gap-5',
						readOnly ? 'cursor-default' : isBusy ? 'pointer-events-none' : 'cursor-pointer'
					)}
					{...interactiveProps}
				>
					<div className="shrink-0 flex items-center justify-center px-10 h-9 w-full">
						<InsurerLogo
							slug={quote.insurer}
							alt={quote.insurer_name}
							className={cn(
								'max-w-full max-h-full w-auto h-9 object-contain',
								isUnavailable && 'grayscale opacity-50'
							)}
						/>
					</div>

					<div className="shrink-0 text-center">
						<div
							className={cn(
								'font-bold text-2xl tabular-nums',
								isUnavailable ? 'text-muted-foreground' : 'text-foreground'
							)}
							data-testid={showRateHeadline ? 'quote-item-premium-rate' : undefined}
						>
							{isUnavailable
								? '...'
								: showRateHeadline
									? `${quote.premium_rate_percentage.toFixed(2)}%`
									: `€ ${Math.round(totalEur).toLocaleString('nl-NL')}`}
						</div>
						<div className="text-muted-foreground text-xs whitespace-nowrap py-1">
							{isUnavailable
								? t('quotes.item.notAvailable')
								: showRateHeadline
									? t('productSelection.quoteCard.premiumRate')
									: t('productSelection.quoteCard.perYear')}
						</div>
						{hasSaving && (
							<div className="text-green-600 text-xs font-semibold mt-1">
								{t('productSelection.quoteCard.saving', {
									percentage: quote.saving_percentage?.toFixed(0)
								})}
							</div>
						)}
					</div>

					{!readOnly && (
						<div className="w-full shrink-0">
							<Button
								type="button"
								variant={isSelected ? 'success' : 'default'}
								size="default"
								className="w-full font-bold px-4 py-2"
								onClick={(e) => {
									e.stopPropagation();
									handleSelect();
								}}
								disabled={isBusy}
								loading={isTransitioning}
								tabIndex={-1}
								aria-hidden
								data-testid="quote-item-select-button"
								data-cy="quote-item-select-button"
							>
								{buttonLabel}
							</Button>
						</div>
					)}
				</div>
				<div className="border-t border-border px-3 py-2 bg-secondary/30">
					<div className="flex w-full items-center justify-between gap-4">
						<a
							href={`${API_URL}/documents/${quote.insurer}/${productType}/card`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-foreground text-xs underline opacity-60 hover:opacity-100 inline-flex items-center gap-2"
						>
							<ExternalLink className="w-3 h-3" />
							{t('quotes.item.insuranceCard')}
						</a>
						<a
							href={`${API_URL}/documents/${quote.insurer}/${productType}/terms`}
							target="_blank"
							rel="noopener noreferrer"
							className="text-foreground text-xs underline opacity-60 hover:opacity-100 inline-flex items-center gap-2"
						>
							<ExternalLink className="w-3 h-3" />
							{t('quotes.item.terms')}
						</a>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Quote;
